'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn } from '@/lib/animations';
import { CheckCircle2, Clock, Thermometer } from 'lucide-react';
import { Button } from './components/ui/button';

export function FoodGradeAbout() {
	const [ref, inView] = useInView({
		triggerOnce: true,
		threshold: 0.05,
		rootMargin: '200px 0px',
	});

	return (
		<section className="py-24 bg-white">
			<div className="container mx-auto px-4 md:px-6">
				<div className="grid lg:grid-cols-2 gap-16 items-center">
					<motion.div
						ref={ref}
						initial="hidden"
						animate={inView ? 'show' : 'hidden'}
						variants={fadeIn('right')}
					>
						<h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">
							Food Grade Storage
						</h2>
						<h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
							Uncompromising Food Safety Standards
						</h3>

						<p className="text-lg text-gray-700 leading-relaxed mb-8">
							Whether you're in need of short-term or long-term food grade storage,
							our facility is designed to meet your product requirements while
							adhering to strict FDA and USDA regulations. From temperature-controlled
							environments to real-time inventory visibility, we offer reliable food
							grade warehousing solutions tailored to your business.
						</p>

						<div className="grid gap-6 mb-8">
							<div className="flex items-start gap-4">
								<div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center text-brand-600 flex-shrink-0">
									<CheckCircle2 size={24} />
								</div>
								<div>
									<h4 className="text-xl font-semibold text-gray-900 mb-2">
										FDA & USDA Compliance
									</h4>
									<p className="text-gray-700">
										Full adherence to federal food safety regulations and
										standards.
									</p>
								</div>
							</div>

							<div className="flex items-start gap-4">
								<div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center text-brand-600 flex-shrink-0">
									<Thermometer size={24} />
								</div>
								<div>
									<h4 className="text-xl font-semibold text-gray-900 mb-2">
										Temperature-Controlled
									</h4>
									<p className="text-gray-700">
										Precise climate control for optimal food product
										preservation.
									</p>
								</div>
							</div>

							<div className="flex items-start gap-4">
								<div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center text-brand-600 flex-shrink-0">
									<Clock size={24} />
								</div>
								<div>
									<h4 className="text-xl font-semibold text-gray-900 mb-2">
										Flexible Storage Terms
									</h4>
									<p className="text-gray-700">
										Short-term and long-term storage options to meet your needs.
									</p>
								</div>
							</div>
						</div>

						<Button
							asChild
							className="bg-brand-600 hover:bg-brand-700 text-white px-8 py-6 text-lg"
						>
							<a href="#contact">Request Quote</a>
						</Button>
					</motion.div>

					<motion.div
						ref={ref}
						initial="hidden"
						animate={inView ? 'show' : 'hidden'}
						variants={fadeIn('left')}
						className="relative"
					>
						<div className="absolute -top-6 -left-6 w-32 h-32 bg-brand-600 rounded-tl-2xl"></div>
						<img
							src="https://lindner-marketing.s3.eu-west-2.amazonaws.com/AdobeStock_377359808-1920x1280.jpeg.webp"
							alt="Food grade warehouse interior"
							className="rounded-lg shadow-xl relative z-10 w-full h-[500px] object-cover"
						/>
						<div className="absolute -bottom-6 -right-6 w-40 h-40 bg-brand-100 rounded-br-2xl"></div>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
